import {
  PlanCurrency,
  PlanEstimateResponse,
  PlanPeriodUnit,
  PlanTypeV2,
  SkedPlanCodeName,
  UpsellPlansType,
} from "./types";
import Axios from "axios";
import { setupCache } from "axios-cache-interceptor";
import { useSelector } from "react-redux";
import { isLegacyPlan, mergeSortPlans } from "./plan-utils";
import { getUser } from "selectors/commonSelectors";
import { User } from "features/user";
import { useEffect, useState } from "react";
import axios from "axios";
import { getAccountsCounts } from "selectors/skedCoreSelectors";

let axiosCachedPlans = setupCache(Axios.create(), {
  ttl: 60 * 60 * 1000,
  methods: ["get", "post"], // Only cache GET and POST, excludes PUT, PATCH, DELETE
});

if (process.env.NODE_ENV === "test") {
  axiosCachedPlans = axios as unknown as typeof axiosCachedPlans;
}

/**
 * Raw response shape from `GET /plan/v2/entitlements-catalog`.
 *
 * Values come live from Chargebee's `item_entitlements_for_item` API —
 * Chargebee is the single source of truth. Missing feature keys are
 * rendered as HTML debug comments on the client so staging/QA can spot
 * Chargebee configs that are out of sync with the UI's expectations.
 */
export interface PlanEntitlementsCatalogEntry {
  displayName: string;
  /** feature_id → CB value. "true"/"false" for switches, numeric strings
   *  for quantities, enum values for custom features (e.g. "advanced"). */
  entitlements: Record<string, string>;
}

export type PlanEntitlementsCatalog = Record<
  string,
  PlanEntitlementsCatalogEntry
>;

/**
 * Fetches Chargebee entitlements for every advertised plan so comparison
 * tables can render without hardcoding any feature map on the client.
 * Cached for an hour via the existing `axiosCachedPlans` instance.
 */
export async function fetchPlanEntitlementsCatalog(): Promise<PlanEntitlementsCatalog> {
  const rs = await axiosCachedPlans.get(`/plan/v2/entitlements-catalog`);
  if (rs.status !== 200) {
    throw new Error(`Error fetching plan entitlements catalog (${rs.status})`);
  }
  const responseData = rs.data as {
    status: string;
    data: { catalog: PlanEntitlementsCatalog };
  };
  if (responseData.status !== "ok") {
    throw new Error("Error fetching plan entitlements catalog");
  }
  return responseData.data.catalog;
}

export async function fetchPlans({
  currency,
  numberAccounts,
  adminId,
}: {
  adminId: string;
  currency: PlanCurrency;
  numberAccounts: number;
}) {
  const rs = await axiosCachedPlans.post(`/user/v2/getPlans`, {
    _id: adminId,
    currency: currency,
    numberAccounts: numberAccounts,
  });

  if (rs.status !== 200) {
    console.error("Error fetching plans", rs);
    throw new Error("Error fetching plans");
  }

  const responseData = rs.data as {
    status: string;
    data: { plans: PlanTypeV2[] };
  };
  if (responseData.status !== "ok") {
    throw new Error("Error fetching plans");
  }
  return responseData.data.plans;
}

export interface TrialEntitlementResponse {
  status: "ok" | "error";
  message?: string;
  code?: number;
  data?: {
    entitlements: string[];
    expires_at: number;
    message?: string;
  };
}

export async function trialEntitlement({
  entitlements,
  expires_at,
}: {
  entitlements: string[];
  expires_at: number;
}): Promise<TrialEntitlementResponse> {
  const rs = await axios.post(
    `/payments/trial-entitlements`,
    {
      entitlements,
      expires_at,
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  if (rs.status !== 200) {
    console.error("Error fetching trial entitlement", rs);
    throw new Error("Error fetching trial entitlement");
  }

  return rs.data;
}

export const useFetchedPlans = (): {
  isLoading: boolean;
  isError: boolean;
  plans: UpsellPlansType;
} => {
  const user = useSelector(getUser) as User;
  const maxAllow = useUserSubscriptionAccountCount();
  const [rawPlans, setRawPlans] = useState<PlanTypeV2[] | undefined>();
  const [isError, setIsError] = useState(false);
  const userPlan = user.plan as SkedPlanCodeName;
  const requestNumberAccounts = userPlan === "sked-fundamentals" ? 1 : maxAllow;

  useEffect(() => {
    async function runFetchPlans(requestNumberAccounts: number) {
      try {
        const { subscription } = user.subscription;
        const plans = await fetchPlans({
          currency: subscription.currency_code as PlanCurrency,
          numberAccounts: requestNumberAccounts,
          adminId: user.adminId,
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setRawPlans(plans);
      } catch {
        setIsError(true);
      }
    }

    if (user?.plan) {
      if (!isLegacyPlan(user) && requestNumberAccounts) {
        runFetchPlans(requestNumberAccounts);
      } else if (isLegacyPlan(user)) {
        setRawPlans([]);
      }
    }
  }, [user, requestNumberAccounts]);

  if (isError) {
    return { isLoading: false, isError: true, plans: { month: [], year: [] } };
  }
  if (!rawPlans) {
    return { isLoading: true, isError: false, plans: { month: [], year: [] } };
  }

  return { isLoading: false, isError: false, plans: mergeSortPlans(rawPlans) };
};

export const updatePlan = async ({
  planId,
  numberAccounts,
  currency,
  billingPeriod,
}: {
  planId: string;
  numberAccounts: number;
  currency: PlanCurrency;
  billingPeriod: PlanPeriodUnit;
}) => {
  const rs = await axios.put(`/user/v2/updateSubscription`, {
    plan: planId,
    billingPeriod,
    numberAccounts,
    currency,
  });
  if (rs.status !== 200) {
    console.error("Error updating plan", rs);
    throw new Error("Error updating plan");
  }

  if (rs.data.status !== "ok") {
    console.error("Error updating plan", rs);
    throw new Error("Error updating plan");
  }

  return rs.data.data.subscription as User["subscription"];
};

export const useUserSubscriptionAccountCount = (): number | undefined => {
  const counts = useSelector(getAccountsCounts);
  return counts.maxAllowed ?? undefined;
};

export const planEstimate = async ({
  planId,
  numberAccounts,
  currency,
  billingPeriod,
  isDowngrade = false,
}: {
  planId: string;
  numberAccounts: number;
  currency: PlanCurrency;
  billingPeriod: PlanPeriodUnit;
  isDowngrade: boolean;
}) => {
  const rs = await axios.post(`/user/v2/getPlanEstimate`, {
    plan: planId,
    billingPeriod,
    numberAccounts,
    currency,
    isDowngrade,
  });
  if (rs.status !== 200) {
    console.error("Error  getPlanEstimate", rs);
    throw new Error("Error getPlanEstimate");
  }
  if (rs.data.status !== "ok") {
    console.error("Error  getPlanEstimate", rs);
    throw new Error("Error getPlanEstimate");
  }
  return rs.data as PlanEstimateResponse;
};
